/**
 * Vendor feeds (PbsDe, Cumwex, Emo) put a youtube link at the end of the product description and
 * the AI generated texts keep it there. The legacy shop turned it into an embed; here the videos
 * are picked out of the html so the page can render them below the description.
 *
 * The link itself is taken out of the text only in the two shapes the data actually has: a bare
 * url on the last line, and a paragraph that holds nothing but the link. Anything else is left as
 * it is and the link stays visible as text, the way it shows today. The html is not parsed beyond
 * telling tags, comments and text apart, so a strange description is never rearranged.
 */
export type DescriptionVideo = { id: string; start?: number; nocookie?: boolean };
export type DescriptionVideos = { html: string; videos: DescriptionVideo[] };

// a comment runs to its --> or, left open, to the end; a tag to the first > outside a quoted
// attribute value, or, cut off, to the end; a stray < is kept as text
const TOKENS = /<!--[\s\S]*?-->|<!--[\s\S]*|<(?:"[^"]*"|'[^']*'|[^"'>])*>|<\/?[a-z][\s\S]*$|[^<]+|</gi;
// a tag name runs to whitespace, a slash or the >, whatever the parser lets it contain
const TAG_NAME = /^<(\/?)([a-z][^\s/>]*)/i;
// the attributes of a tag in order, quoted values kept whole so an href inside one is not an href
const ATTRIBUTE = /([^\s=>]+)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+)))?/g;
// a bare url starts the text or follows something that is not part of a word, in any script
const BARE_URL = /(^|[^\p{L}\p{N}_.-])(https?:\/\/[^\s<"'()[\]{}]+)/giu;
// an ampersand written as an entity is a query separator; any other entity ends the url
const AMPERSAND = /&(?:amp|AMP|#0*38|#[xX]0*26);/g;
const ENTITY_AFTER_URL = /&(?!amp;|AMP;|#0*38;|#[xX]0*26;)(?:[a-zA-Z]+|#\d+|#[xX][0-9a-fA-F]+);/;
// sentence punctuation, the typographic kind included, that rich text puts right after a url
const TRAILING_PUNCTUATION = /[.,;:!?“”‘’«»…]+$/;
const VIDEO_ID = /^[A-Za-z0-9_-]{11}$/;
// whitespace as rich text writes it, entities included, which is nothing on the page; entity
// names are case-sensitive in html, only the hex digits are not
const PADDING = /^(?:\s|&nbsp;|&#0*(?:160|32|9|10|13);|&#[xX]0*(?:[aA]0|20|9|[aA]|[dD]);|&Tab;|&NewLine;)*$/;
// the character references a url is spelled with; names are case-sensitive as in html, the
// hexadecimal digits are not
const REFERENCE =
	/&(?:#[xX]([0-9a-fA-F]+)|#(\d+)|(amp|lt|gt|quot|apos|nbsp|colon|sol|num|quest|equals|period|percnt|plus|semi|lowbar));/g;
const NAMED: Record<string, string> = {
	amp: '&',
	lt: '<',
	gt: '>',
	quot: '"',
	apos: "'",
	nbsp: ' ',
	colon: ':',
	sol: '/',
	num: '#',
	quest: '?',
	equals: '=',
	period: '.',
	percnt: '%',
	plus: '+',
	semi: ';',
	lowbar: '_',
};
// elements whose content is not prose: raw text that runs to the element's own end tag, a
// hidden template that may nest, and code that may nest too
const RAW_TEXT = new Set(['script', 'style', 'textarea', 'title', 'xmp', 'iframe', 'noembed', 'noframes', 'plaintext']);
const NESTING = new Set(['template', 'code', 'pre']);
const SKIPPED = new Set([...RAW_TEXT, ...NESTING]);
// what may stand beside a link in a paragraph that is otherwise empty
const INLINE = new Set(['br', 'strong', 'b', 'em', 'i', 'u', 's', 'span', 'small', 'sub', 'sup', 'abbr']);

type Tag = { closing: boolean; name: string };
const tagOf = (token: string): Tag | null => {
	const match = token[0] === '<' ? TAG_NAME.exec(token) : null;
	return match ? { closing: match[1] === '/', name: match[2].toLowerCase() } : null;
};
const isComment = (token: string) => token.startsWith('<!--');
const isText = (token: string) => token[0] !== '<';

// a code point outside unicode, or a lone surrogate, becomes the replacement character as in a browser
const fromCodePoint = (code: number) =>
	code > 0 && code <= 0x10ffff && (code < 0xd800 || code > 0xdfff) ? String.fromCodePoint(code) : '�';
const decodeReferences = (value: string) =>
	value.replace(REFERENCE, (_ref, hex: string, dec: string, name: string) =>
		hex ? fromCodePoint(parseInt(hex, 16)) : dec ? fromCodePoint(Number(dec)) : NAMED[name]
	);

/** the value of a tag's href attribute, decoded, or null */
function hrefOf(tag: string): string | null {
	const body = tag.replace(/^<[a-z][^\s/>]*|\/?>$/gi, '');
	// exec rather than matchAll, which the Safari 12 the legacy build targets does not have
	ATTRIBUTE.lastIndex = 0;
	for (let match = ATTRIBUTE.exec(body); match !== null; match = ATTRIBUTE.exec(body)) {
		if (match[0] === '') ATTRIBUTE.lastIndex++;
		if (match[1].toLowerCase() === 'href') return decodeReferences(match[2] ?? match[3] ?? match[4] ?? '');
	}
	return null;
}

// a playback position as youtube writes it: 90, 90s or 1h2m3s
const START = /^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s?)?$/;
function parseStart(value: string | null): number | undefined {
	const match = value ? START.exec(value) : null;
	if (!match) return undefined;
	const seconds = Number(match[1] ?? 0) * 3600 + Number(match[2] ?? 0) * 60 + Number(match[3] ?? 0);
	return seconds > 0 ? seconds : undefined;
}

/** the video in a watch, embed, shorts or youtu.be url, with its start position, or null */
export function youtubeVideo(rawUrl: string): DescriptionVideo | null {
	let url: URL;
	try {
		// the base only resolves protocol-relative links; anything relative lands on a host that is not youtube
		url = new URL(rawUrl.replace(AMPERSAND, '&'), 'https://example.invalid');
	} catch {
		return null;
	}
	if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
	const host = url.hostname.replace(/^(?:www|m)\./, '');
	const path = url.pathname.replace(/\/+$/, '');
	const nocookie = host === 'youtube-nocookie.com';
	let id: string | null = null;
	if (host === 'youtube.com' || nocookie) {
		const inPath = /^\/(?:embed|shorts|v|live)\/([^/]+)$/.exec(path);
		if (path === '/watch') id = url.searchParams.get('v');
		else if (inPath) id = inPath[1];
	} else if (host === 'youtu.be') {
		id = path.slice(1);
	}
	// embed/videoseries is a playlist, not a video
	if (!id || !VIDEO_ID.test(id) || id === 'videoseries') return null;
	const video: DescriptionVideo = { id };
	// the position may also sit in the fragment, as youtube's own share links once wrote it
	const fragment = /^#t=([^&]+)/.exec(url.hash);
	const start = parseStart(
		url.searchParams.get('t') ?? url.searchParams.get('start') ?? (fragment ? fragment[1] : null)
	);
	if (start) video.start = start;
	if (nocookie) video.nocookie = true;
	return video;
}

type BareLink = { url: string; video: DescriptionVideo; from: number; to: number };

/** the videos among the bare urls in a piece of text, each with where its url sits */
function bareVideos(text: string): BareLink[] {
	const found: BareLink[] = [];
	BARE_URL.lastIndex = 0;
	for (let match = BARE_URL.exec(text); match !== null; match = BARE_URL.exec(text)) {
		const candidate = match[2];
		const entity = ENTITY_AFTER_URL.exec(candidate);
		const url = (entity ? candidate.slice(0, entity.index) : candidate).replace(TRAILING_PUNCTUATION, '');
		const video = youtubeVideo(url);
		if (!video) continue;
		const from = match.index + match[1].length;
		found.push({ url, video, from, to: from + url.length });
	}
	return found;
}

/** the text with the given links cut out, in one pass */
function without(text: string, links: BareLink[]): string {
	let rest = '';
	let at = 0;
	for (const link of links) {
		rest += text.slice(at, link.from);
		at = link.to;
	}
	return rest + text.slice(at);
}

/**
 * @param limit how many videos to pick out; a link beyond it is left alone entirely
 */
export function extractDescriptionVideos(description: string, limit = Infinity): DescriptionVideos {
	const tokens = description.match(TOKENS) ?? [];
	const videos: DescriptionVideo[] = [];
	const seen = new Set<string>();
	// the same video at another position, or in the other player, is another entry
	const keyOf = (video: DescriptionVideo) => `${video.id}|${video.start ?? 0}|${video.nocookie ? 1 : 0}`;
	// whether the link is ours: yes for one already picked, no once the limit is reached
	const take = (video: DescriptionVideo) => {
		if (seen.has(keyOf(video))) return true;
		if (videos.length >= limit) return false;
		seen.add(keyOf(video));
		videos.push(video);
		return true;
	};
	// text that is nothing but picked links and padding once they are gone
	const onlyLinks = (text: string) => {
		const links = bareVideos(text).filter((l) => seen.has(keyOf(l.video)));
		return links.length > 0 && PADDING.test(without(text, links));
	};
	const isFiller = (token: string) => {
		if (isText(token)) return PADDING.test(token);
		if (isComment(token)) return true;
		const tag = tagOf(token);
		return tag !== null && INLINE.has(tag.name);
	};

	/*
	 * A paragraph that holds nothing but the link, as the AI texts write it: from the <p> at
	 * `start`, filler, one link (an anchor with its label, or a bare url), filler, </p>. Returns
	 * the index of the </p> and the video, or null.
	 */
	const linkOnlyParagraph = (start: number): { end: number; video: DescriptionVideo } | null => {
		let video: DescriptionVideo | null = null;
		for (let j = start + 1; j < tokens.length; j++) {
			const token = tokens[j];
			const tag = tagOf(token);
			if (tag?.closing && tag.name === 'p') return video ? { end: j, video } : null;
			if (isFiller(token)) continue;
			if (video) return null;
			if (tag && !tag.closing && tag.name === 'a') {
				video = youtubeVideo(hrefOf(token) ?? '');
				if (!video) return null;
				// the label may be text and formatting, up to the anchor's own end tag
				for (j++; j < tokens.length; j++) {
					const inner = tagOf(tokens[j]);
					if (inner?.closing && inner.name === 'a') break;
					if (inner && !INLINE.has(inner.name)) return null;
				}
				if (j >= tokens.length) return null;
			} else if (isText(token)) {
				const links = bareVideos(token);
				if (links.length !== 1 || !PADDING.test(without(token, links))) return null;
				video = links[0].video;
			} else {
				return null;
			}
		}
		return null;
	};

	const keep = tokens.map(() => true);
	// text that is the label of a link or sits in a skipped element, never dropped as a bare link line
	const label = tokens.map(() => false);
	const skipped: string[] = [];
	// text inside a link to something else is that link's label, not a bare url
	let insideOtherAnchor = false;
	let insideAnchor = false;
	for (let i = 0; i < tokens.length; i++) {
		const token = tokens[i];
		const tag = tagOf(token);
		if (skipped.length > 0) {
			const inside = skipped[skipped.length - 1];
			if (isText(token)) label[i] = true;
			// nothing ends a plaintext, not even its own end tag
			if (tag?.closing && tag.name === inside && inside !== 'plaintext') skipped.pop();
			else if (tag && !tag.closing && NESTING.has(tag.name) && !RAW_TEXT.has(inside)) skipped.push(tag.name);
			continue;
		}
		// after the skipped content, so tag-like text in a script cannot open or close an anchor
		if (tag?.name === 'a') {
			insideOtherAnchor = false;
			insideAnchor = !tag.closing;
		}
		if (tag && !tag.closing && SKIPPED.has(tag.name)) {
			skipped.push(tag.name);
		} else if (tag && !tag.closing && tag.name === 'p') {
			const paragraph = linkOnlyParagraph(i);
			if (paragraph && take(paragraph.video)) {
				for (let j = i; j <= paragraph.end; j++) keep[j] = false;
				i = paragraph.end;
			}
		} else if (tag && !tag.closing && tag.name === 'a') {
			const video = youtubeVideo(hrefOf(token) ?? '');
			if (video) take(video);
			else insideOtherAnchor = true;
		} else if (isText(token)) {
			// a label is never dropped as a bare link line, and a label of another link is no bare url
			if (insideAnchor) label[i] = true;
			if (!insideOtherAnchor) for (const link of bareVideos(token)) take(link.video);
		}
	}

	/*
	 * A bare url on the last line, as the vendor feeds write it: dropped together with the line
	 * breaks and padding around it, looking past the end tags that close the description.
	 */
	// whether nothing but padding stands between the token and the start of its line, so the
	// links in it are the whole line and not the tail of "Watch: <url>"
	const startsLine = (index: number) => {
		for (let j = index - 1; j >= 0; j--) {
			const before = tokens[j];
			if (isComment(before) || (isText(before) && PADDING.test(before))) continue;
			const tag = tagOf(before);
			return tag !== null && (tag.name === 'br' || (!tag.closing && !INLINE.has(tag.name)));
		}
		return true;
	};
	let dropped = false;
	// the breaks and padding walked past since the last dropped line, which go with the next one
	let padding: number[] = [];
	for (let i = tokens.length - 1; i >= 0; i--) {
		if (!keep[i]) continue;
		const token = tokens[i];
		const tag = tagOf(token);
		if (tag?.closing || isComment(token)) continue;
		if (tag?.name === 'br' || (isText(token) && PADDING.test(token))) {
			if (dropped) keep[i] = false;
			else padding.push(i);
			continue;
		}
		if (isText(token) && !label[i] && onlyLinks(token) && startsLine(i)) {
			keep[i] = false;
			dropped = true;
			for (const j of padding) keep[j] = false;
			padding = [];
			continue;
		}
		break;
	}

	if (videos.length === 0) return { html: description, videos };
	return { html: tokens.filter((_t, i) => keep[i]).join(''), videos };
}
